// =============================================================================
// src/features/explore/explore-route.ts — Abertura/fechamento da rota Explorar.
//
// Proveniencia: assets/explore.js `var ExploreRoute = {...}` (linhas 665-688) e o
//   `boot()` (693-699). Porte FIEL: alterna a classe body.is-explore-view, fecha as
//   rotas irmas (Grafo/Workspace) e o Modo Cinema, cria o mapa em duplo
//   requestAnimationFrame (espera o layout estabilizar) e sincroniza a topbar.
//   Sem timings inventados — todos espelham o legado.
//
// init() substitui o auto-boot do IIFE: main.ts chama init() apos montar o DOM.
//   Por deep-link `?view=grafo|workspace` a Explorar NAO abre (igual ao boot legado).
//   Publica a instancia em window.ExploreRoute para a leitura cross-modulo durante a
//   coexistencia (ExploreUI le getExploreRouteGlobal()).
// =============================================================================

import type { AtalaiaClient } from '../../app/types';
import { qs } from '../../app/dom';
import { ExploreMap } from './explore-map';
import { ExploreUI, bindExploreUi } from './explore-ui';
import { getTopbarNav, getMapController, getGrafoRoute, getWorkspaceRoute } from './explore-globals';

/** Classe no <body> que ativa o layout fullscreen da Explorar. */
const EXPLORE_VIEW_CLASS = 'is-explore-view';
/** Classe do Modo Cinema (removida ao abrir a Explorar). */
const CINEMA_ACTIVE_CLASS = 'cinema-active';
/** Ms ate re-medir o mapa principal apos fechar a rota (assets/explore.js 685). */
const MAP_INVALIDATE_DELAY_MS = 60;

/** Controlador da rota Explorar (open/close/init). Singleton exportado ao final. */
class ExploreFullscreenRoute {
  private page: HTMLElement | null = null;

  /** Resolve a pagina, injeta o cliente AtalaIA no mapa e liga a UI (assets/explore.js 667). */
  init(atalaia: AtalaiaClient | null = null): void {
    this.page = qs<HTMLElement>('#u-explore-page');
    ExploreMap.bindAtalaia(atalaia);
    bindExploreUi();
    ExploreUI.init();
  }

  /** True quando a rota Explorar esta visivel (assets/explore.js 668). */
  isOpen(): boolean {
    return document.body.classList.contains(EXPLORE_VIEW_CLASS);
  }

  /** Abre a rota: fecha as irmas, ativa o layout e cria/mede o mapa (assets/explore.js 669-678). */
  open(): void {
    if (this.isOpen()) return;
    this.closeSiblingRoutes();
    document.body.classList.remove(CINEMA_ACTIVE_CLASS);
    document.body.classList.add(EXPLORE_VIEW_CLASS);
    this.page?.setAttribute('aria-hidden', 'false');
    this.ensureMapAfterLayout();
    getTopbarNav()?.syncActive();
  }

  /** Fecha Grafo/Workspace se estiverem abertas (assets/explore.js 671-672). */
  private closeSiblingRoutes(): void {
    const grafo = getGrafoRoute();
    if (grafo?.isOpen?.()) grafo.close();
    const workspace = getWorkspaceRoute();
    if (workspace?.isOpen?.()) workspace.close();
  }

  /** Cria o mapa em duplo rAF para esperar o layout fullscreen (assets/explore.js 676). */
  private ensureMapAfterLayout(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => ExploreMap.ensure()));
  }

  /** Fecha a rota, limpa menus/card/selecao e re-mede o mapa principal (assets/explore.js 679-687). */
  close(): void {
    if (!this.isOpen()) return;
    document.body.classList.remove(EXPLORE_VIEW_CLASS);
    this.page?.setAttribute('aria-hidden', 'true');
    ExploreUI.closeMenu();
    ExploreUI.hideCard();
    if (ExploreMap.ready) ExploreMap.clearSelection();
    this.invalidateMainMap();
    getTopbarNav()?.syncActive();
  }

  /** Re-mede o mapa principal apos a transicao de fechamento. */
  private invalidateMainMap(): void {
    const controller = getMapController();
    if (controller?.invalidateSize) setTimeout(() => controller.invalidateSize?.(), MAP_INVALIDATE_DELAY_MS);
  }
}

/** Singleton da rota Explorar — substitui o `var ExploreRoute`/`window.ExploreRoute`. */
export const ExploreRoute = new ExploreFullscreenRoute();

/** Re-export do mapa para o barrel (espelha window.ExploreMap legado). */
export { ExploreMap } from './explore-map';

/** Janela com os globais que a feature publica para leitura cross-modulo. */
interface ExplorePublishWindow {
  ExploreRoute?: ExploreFullscreenRoute;
  ExploreMap?: typeof ExploreMap;
}

/**
 * Inicializa a feature Explorar (porte do `boot()` legado): liga UI/route e publica
 * os singletons em window p/ a leitura cross-modulo durante a coexistencia.
 * Reforma 2026-06 (D2): a decisao de landing saiu daqui — main.ts resolve via
 * resolveLandingView(src/app/landing-view.ts) e passa openAsLanding (o Briefing
 * e o novo default; a Explorar abre por deep-link ?view=explore).
 *
 * @example
 *   import { init as initExplore } from './features/explore';
 *   initExplore(resolveLandingView(location.search) === 'explore', context.atalaia);
 */
export function init(openAsLanding: boolean, atalaia: AtalaiaClient | null = null): void {
  publishExploreGlobals();
  ExploreRoute.init(atalaia);
  if (openAsLanding) ExploreRoute.open();
}

/** Publica ExploreRoute/ExploreMap no window (paridade com o IIFE legado). */
function publishExploreGlobals(): void {
  const win = window as unknown as ExplorePublishWindow;
  win.ExploreRoute = ExploreRoute;
  win.ExploreMap = ExploreMap;
}
